<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section class="hero-section relative h-screen flex items-center justify-center overflow-hidden">
      <!-- Background Image -->
      <div class="absolute inset-0 w-full h-full">
        <img :src="backgroundImage" alt="Services Background" class="w-full h-full object-cover" />
        <!-- Overlay for better text readability -->
        <div class="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
      </div>
      
      <!-- Content Container -->
      <div class="relative z-10 text-center px-4 max-w-6xl mx-auto">
        
        <!-- Main Title with Modern Typography -->
        <h1 class="text-3xl md:text-5xl font-heading tracking-tight mb-6">
          <span class="block text-white font-light">{{ $t('services.hero.titlePart1') }}</span>
          <span class="block font-bold text-white">{{ $t('services.hero.titlePart2') }}</span>
        </h1>
        
        <!-- Subtitle with Enhanced Styling -->
        <p class="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed text-justify">
          {{ $t('services.hero.subtitle') }}
        </p>
        
        
        
        
      </div>
    </section>

    <!-- Main Content -->
    <main>
      <!-- Services Overview -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16">
            <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('services.overview.label') }}</span>
            <h2 class="text-3xl md:text-4xl font-heading tracking-tight mt-4 font-bold text-white">{{ $t('services.overview.title') }}</h2>
            <p class="text-xl text-gray-300 mt-4 max-w-3xl mx-auto text-justify">
              {{ $t('services.overview.description') }}
            </p>
          </div>

          <!-- Services Slider -->
          <div class="relative">
            <Swiper
              :modules="[Navigation, Pagination, Autoplay]"
              :slides-per-view="1"
              :space-between="24"
              :breakpoints="{
                640: { slidesPerView: 1, spaceBetween: 24 },
                768: { slidesPerView: 2, spaceBetween: 32 },
                1024: { slidesPerView: 3, spaceBetween: 32 }
              }"
              :navigation="{
                nextEl: '.swiper-button-next-services',
                prevEl: '.swiper-button-prev-services'
              }"
              :pagination="{
                clickable: true,
                el: '.swiper-pagination-services'
              }"
              :autoplay="{
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }"
              :loop="true"
              class="services-swiper"
              @swiper="onSwiper"
            >
              <SwiperSlide v-for="(service, index) in services" :key="index">
                <div class="group relative bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full border border-gray-400">
                  <!-- Service Icon Container -->
                  <div class="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-primary-dark"></div>
                  
                  <!-- Service Content -->
                  <div class="p-8 h-full flex flex-col">
                    <div class="text-primary mb-6 transform group-hover:scale-110 transition-transform duration-300">
                      <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="service.icon"></path>
                        </svg>
                      </div>
                    </div>
                    
                <h3 class="text-2xl font-heading mb-4 font-bold text-white group-hover:scale-105 transition-all duration-300">
                  {{ service.title }}
                </h3>
                <p class="text-gray-300 mb-6 flex-grow text-justify">{{ service.description }}</p>
                    
                    <!-- Service Features -->
                    <ul class="space-y-3 mb-6">
                    <li v-for="(feature, featureIndex) in service.features" :key="featureIndex" 
                        class="flex items-center text-gray-300">
                        <svg class="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        {{ feature }}
                      </li>
                    </ul>
                    
                    <!-- CTA Button -->
                    <div class="mt-auto">
                      <button @click="showServiceDetails(service)"
                              class="inline-flex items-center text-primary font-semibold">
                        {{ $t('services.modal.learnMore') }}
                        <svg class="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                             fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            <!-- Custom Navigation Buttons -->
            <div class="swiper-button-prev-services absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-800 hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer group">
              <svg class="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </div>
            
            <div class="swiper-button-next-services absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-800 hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer group">
              <svg class="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>

            <!-- Custom Pagination -->
            <div class="swiper-pagination-services flex justify-center mt-8 space-x-2"></div>
          </div>
        </div>
      </section>

      <!-- Service Details Modal -->
      <div v-if="selectedService" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div class="p-8">
            <div class="flex justify-between items-start mb-6">
              <div>
                <h3 class="text-2xl font-heading font-bold text-white mb-2">{{ selectedService.title }}</h3>
                <p class="text-gray-300">{{ selectedService.description }}</p>
              </div>
              <button @click="selectedService = null" class="text-gray-400">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 class="text-xl font-semibold mb-4 font-bold text-white">{{ $t('services.modal.mainFeatures') }}</h4>
                <ul class="space-y-3">
                  <li v-for="(feature, index) in selectedService.features" :key="index"
                      class="flex items-center text-gray-300">
                    <svg class="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {{ feature }}
                  </li>
                </ul>
              </div>

              <div>
                <h4 class="text-xl font-semibold mb-4 font-bold text-white">{{ $t('services.modal.benefits') }}</h4>
                <ul class="space-y-3">
                  <li v-for="(benefit, index) in selectedService.benefits" :key="index"
                      class="flex items-center text-gray-300">
                    <svg class="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {{ benefit }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="mt-8">
              <router-link to="/contact" 
                          class="btn-primary inline-flex items-center px-6 py-3 text-lg font-medium rounded-lg">
                {{ $t('services.modal.contactUs') }}
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Why Choose Us -->
      <section class="py-24 bg-gray-800">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16">
            <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('services.whyChooseUs.label') }}</span>
            <h2 class="text-3xl md:text-4xl font-heading tracking-tight mt-4 font-bold text-white">{{ $t('services.whyChooseUs.title') }}</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div v-for="(reason, index) in whyChooseUs" :key="index"
                 class="bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-400">
              <div class="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="reason.icon"></path>
                </svg>
              </div>
                              <h3 class="text-xl font-heading mb-4 font-bold text-white">{{ reason.title }}</h3>
              <p class="text-gray-300">{{ reason.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Service Models -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-heading tracking-tight mb-4 font-bold text-white">{{ $t('services.serviceModels.title') }}</h2>
            <p class="text-xl text-gray-300 max-w-3xl mx-auto text-justify">{{ $t('services.serviceModels.subtitle') }}</p>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Flat-Rate Service Model -->
            <div class="bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border-2 border-primary/30">
              <div class="mb-6">
                <h3 class="text-2xl font-heading mb-3 font-bold text-white">
                  {{ $t('services.serviceModels.flatRate.title') }}
                </h3>
                <div class="inline-flex items-center px-4 py-2 bg-primary/20 rounded-lg mb-4">
                  <svg class="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-primary font-semibold">{{ $t('services.serviceModels.flatRate.timeFrame') }}</span>
                </div>
              </div>
              
              <p class="text-gray-200 mb-6 leading-relaxed text-justify">
                {{ $t('services.serviceModels.flatRate.description') }}
              </p>

              <ul class="space-y-3 mb-6">
                <li v-for="(feature, index) in flatRateFeatures" :key="index" 
                    class="flex items-start text-gray-200">
                  <svg class="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{{ feature }}</span>
                </li>
              </ul>

              <p class="text-gray-200 leading-relaxed italic mb-6 text-justify">
                {{ $t('services.serviceModels.flatRate.conclusion') }}
              </p>

              <router-link to="/contact" 
                          class="inline-flex items-center justify-center w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors duration-300 shadow-md hover:shadow-lg">
                {{ $t('services.serviceModels.flatRate.contactForPrice') }}
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </router-link>
            </div>

            <!-- Activity-Based Service Model -->
            <div class="bg-gradient-to-br from-gray-800 to-gray-700 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-600">
              <div class="mb-6">
                <h3 class="text-2xl font-heading mb-4 font-bold text-white">
                  {{ $t('services.serviceModels.activityBased.title') }}
                </h3>
              </div>
              
              <p class="text-gray-200 mb-6 leading-relaxed text-justify">
                {{ $t('services.serviceModels.activityBased.description') }}
              </p>

              <ul class="space-y-3 mb-6">
                <li v-for="(activity, index) in activityBasedActivities" :key="index" 
                    class="flex items-start text-gray-200">
                  <svg class="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                  <span>{{ activity }}</span>
                </li>
              </ul>

              <p class="text-gray-200 leading-relaxed italic mb-6 text-justify">
                {{ $t('services.serviceModels.activityBased.conclusion') }}
              </p>

              <router-link to="/contact" 
                          class="inline-flex items-center justify-center w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors duration-300 shadow-md hover:shadow-lg">
                {{ $t('services.serviceModels.activityBased.contactForPrice') }}
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <!-- Call to Action -->
      <section class="py-24 bg-primary text-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                      <h2 class="text-3xl md:text-5xl font-heading tracking-tight mb-6 font-bold text-white">{{ $t('services.cta.title') }}</h2>
          <p class="text-xl text-gray-200 mb-8 max-w-2xl mx-auto text-justify">
            {{ $t('services.cta.description') }}
          </p>
          <router-link to="/contact" 
                      class="inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg bg-white text-primary hover:bg-gray-100 transition-colors duration-300">
            {{ $t('services.cta.button') }}
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'
import backgroundImage from '../assets/bg_img2.jpeg'

const { t, locale } = useI18n()
const route = useRoute()
const selectedService = ref(null)
const swiperInstance = ref(null)

// Helper function to get array translations
const getArrayTranslation = (key) => {
  const keys = key.split('.')
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  let value = messages
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} at ${k}`)
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

// Service icons mapping
const serviceIcons = {
  infrastructure: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
  helpdesk: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z',
  project: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  development: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  optimization: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  data: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
  dispatch: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
  itsm: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  itad: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  itam: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
}

// Default icon if service doesn't have one defined
const defaultIcon = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'

// Dynamically generate services from translation files
const services = computed(() => {
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  // Get services.items from translations
  const servicesItems = messages?.services?.items
  
  if (!servicesItems || typeof servicesItems !== 'object') {
    console.warn('No services.items found in translations')
    return []
  }
  
  // Build services array dynamically from translation keys
  return Object.keys(servicesItems)
    .filter(key => {
      // Only include services that have title and description (valid service objects)
      const service = servicesItems[key]
      return service && typeof service === 'object' && service.title && service.description
    })
    .map(key => {
      const service = servicesItems[key]
      return {
        key: key, // Store the key for reference
        title: service.title,
        description: service.description,
        icon: serviceIcons[key] || defaultIcon,
        features: Array.isArray(service.features) ? service.features : [],
        benefits: Array.isArray(service.benefits) ? service.benefits : []
      }
    })
})

const whyChooseUs = computed(() => [
  {
    title: t('services.whyChooseUs.expertise.title'),
    description: t('services.whyChooseUs.expertise.description'),
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    title: t('services.whyChooseUs.solutions.title'),
    description: t('services.whyChooseUs.solutions.description'),
    icon: 'M13 10V3L4 14h7v7l9-11h-7z'
  },
  {
    title: t('services.whyChooseUs.support.title'),
    description: t('services.whyChooseUs.support.description'),
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
  }
])

const flatRateFeatures = computed(() => {
  return getArrayTranslation('services.serviceModels.flatRate.features')
})

const activityBasedActivities = computed(() => {
  return getArrayTranslation('services.serviceModels.activityBased.activities')
})

const onSwiper = (swiper) => {
  swiperInstance.value = swiper
  // Check if there's a service parameter in the URL
  const serviceKey = route.query.service
  if (serviceKey) {
    navigateToService(serviceKey)
  }
}

const navigateToService = (serviceKey) => {
  if (!swiperInstance.value) return
  
  const serviceIndex = services.value.findIndex(s => s.key === serviceKey)
  if (serviceIndex !== -1) {
    // Wait for swiper to be ready, then navigate to the service
    nextTick(() => {
      swiperInstance.value.slideTo(serviceIndex, 500)
      // Scroll to the services section
      setTimeout(() => {
        const servicesSection = document.querySelector('.services-swiper')
        if (servicesSection) {
          servicesSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 600)
    })
  }
}

const showServiceDetails = (service) => {
  selectedService.value = service
}

// Watch for route changes to handle service parameter
watch(() => route.query.service, (serviceKey) => {
  if (serviceKey && swiperInstance.value) {
    navigateToService(serviceKey)
  }
})

onMounted(() => {
  // Check for service parameter on mount
  const serviceKey = route.query.service
  if (serviceKey) {
    // Wait a bit for swiper to initialize
    setTimeout(() => {
      navigateToService(serviceKey)
    }, 500)
  }
})
</script>

<style scoped>
/* Swiper Custom Styles */
.services-swiper {
  --swiper-navigation-size: 48px;
  --swiper-navigation-color: #008067;
  --swiper-pagination-color: #008067;
  --swiper-pagination-bullet-size: 12px;
  --swiper-pagination-bullet-horizontal-gap: 8px;
}

.services-swiper .swiper-slide {
  height: auto;
  display: flex;
}

/* Custom Navigation Buttons */
.swiper-button-prev-services,
.swiper-button-next-services {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.swiper-button-prev-services:hover,
.swiper-button-next-services:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-50%) scale(1.1);
}

.swiper-button-prev-services {
  left: -24px;
}

.swiper-button-next-services {
  right: -24px;
}

/* Custom Pagination */
.swiper-pagination-services {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
}

.swiper-pagination-services .swiper-pagination-bullet {
  width: 12px;
  height: 12px;
  background: #e5e7eb;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 1;
}

.swiper-pagination-services .swiper-pagination-bullet-active {
  background: #008067;
  transform: scale(1.2);
}

.swiper-pagination-services .swiper-pagination-bullet:hover {
  background: #008067;
  transform: scale(1.1);
}

/* Responsive Navigation */
@media (max-width: 768px) {
  .swiper-button-prev-services,
  .swiper-button-next-services {
    width: 40px;
    height: 40px;
  }
  
  .swiper-button-prev-services {
    left: 8px;
  }
  
  .swiper-button-next-services {
    right: 8px;
  }
}

/* Custom styles for the services page */
.btn-primary {
  @apply bg-primary text-white hover:bg-primary-dark focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200;
}

/* Service Card Styles */
.service-card {
  @apply bg-gray-800 rounded-2xl shadow-lg p-8 transition-all duration-300 hover:shadow-xl border border-gray-400;
}

/* Feature List Styles */
.feature-list {
  @apply space-y-3;
}

.feature-item {
  @apply flex items-center text-gray-600;
}

.feature-icon {
  @apply w-5 h-5 text-primary mr-2;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4;
}

.modal-content {
  @apply bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto;
}

/* Why Choose Us Card Styles */
.why-card {
  @apply bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-400;
}

.why-icon {
  @apply w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6;
}

/* CTA Section Styles */
.cta-section {
  @apply py-24 bg-primary text-white;
}

.cta-button {
  @apply inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg bg-white text-primary hover:bg-gray-100 transition-colors duration-300;
}
</style> 